import { useEffect, useState, useCallback } from "react";
import { Loader, Modal } from "rsuite";
import {
  Divider, Select, Card, Row, Col, Statistic, Tag, Tooltip,
  Button, Space, Segmented, Empty, Popconfirm, Tabs, Badge,
  message
} from "antd";
import {
  FilePdfOutlined, FileExcelOutlined, CalendarOutlined,
  PlayCircleOutlined, TableOutlined, UnorderedListOutlined,
  ClockCircleOutlined, HomeOutlined, TeamOutlined, BookOutlined,
  ReloadOutlined, UserOutlined, DragOutlined,
  EditOutlined, DeleteOutlined,
  WarningOutlined
} from "@ant-design/icons";
import { useNotification } from "../../../utils/notificationProvider";
import { appCRUD } from "../../../actions/app";
import { char } from "../../../utils";

const { Option } = Select;

// Programme colors for visual distinction
const PROGRAMME_COLORS = {
  "BSC-CS": { bg: "#e6f7ff", border: "#1890ff", text: "#0050b3" },
  "DIP-CS": { bg: "#f6ffed", border: "#52c41a", text: "#237804" },
  "DIP-IT": { bg: "#fff7e6", border: "#fa8c16", text: "#ad4e00" },
  "BSC-ITAM": { bg: "#f9f0ff", border: "#722ed1", text: "#391085" },
  "default": { bg: "#fafafa", border: "#d9d9d9", text: "#595959" },
};

const YEAR_COLORS = {
  1: "#52c41a",
  2: "#1890ff",
  3: "#722ed1",
  4: "#fa8c16",
};

const DAY_ORDER = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

export const TimeTableView = (props) => {
  const { open, setOpen, dataId } = props;
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const openNotification = useNotification();
  const [day, setDay] = useState("Monday");
  const [data, setData] = useState([]);
  const [allData, setAllData] = useState([]); // All week data for filtered views
  const [viewMode, setViewMode] = useState("grid");
  const [activeTab, setActiveTab] = useState("day"); // day, instructor, cohort

  // Filters
  const [programmeFilter, setProgrammeFilter] = useState(null);
  const [roomFilter, setRoomFilter] = useState(null);
  const [instructorFilter, setInstructorFilter] = useState(null);
  const [studyYearFilter, setStudyYearFilter] = useState(null);

  // Drag and drop state
  const [draggedItem, setDraggedItem] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [conflictCheckResult, setConflictCheckResult] = useState(null);
  const [updating, setUpdating] = useState(false);

  // Available rooms for dropdown
  const [availableRooms, setAvailableRooms] = useState([]);

  // Time slots - 8:00 to 20:00
  const hours = Array.from({ length: 12 }, (_, i) => 8 + i);
  const days = DAY_ORDER;

  // Conflicts state
  const [conflictsOpen, setConflictsOpen] = useState(false);
  const [conflictsData, setConflictsData] = useState(null);
  const [loadingConflicts, setLoadingConflicts] = useState(false);

  const loadConflicts = useCallback(() => {
    if (!dataId) return;
    setLoadingConflicts(true);
    appCRUD.loadTimetableConflicts(dataId)
      .then((resp) => setConflictsData(resp.data))
      .catch(() => setConflictsData(null))
      .finally(() => setLoadingConflicts(false));
  }, [dataId]);

  // Load day data
  const loadDayData = useCallback((selectedDay) => {
    setLoading(true);
    appCRUD
      .loadTableDayByDay(dataId, selectedDay.toLowerCase())
      .then((resp) => {
        const results = resp.data.results || resp.data || [];
        setData(Array.isArray(results) ? results : []);
      })
      .catch((error) => {
        openNotification("error", "Error", error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [dataId, openNotification]);

  // Load all week data for filtered views
  const loadAllData = useCallback(() => {
    setLoading(true);
    appCRUD
      .loadTableDayByTable(dataId)
      .then((resp) => {
        const results = resp.data.results || resp.data || [];
        setAllData(Array.isArray(results) ? results : []);
      })
      .catch((error) => {
        openNotification("error", "Error", error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [dataId, openNotification]);

  // Load available rooms
  const loadRooms = useCallback(() => {
    appCRUD.loadRoom(100, 0, "", "code")
      .then((resp) => {
        const results = resp.data.results || resp.data || [];
        setAvailableRooms(Array.isArray(results) ? results : []);
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (dataId && open) {
      loadRooms();
      loadConflicts();
      if (activeTab === "day") {
        loadDayData(day);
      } else {
        loadAllData();
      }
    }
  }, [dataId, open, day, activeTab, loadDayData, loadAllData, loadRooms, loadConflicts]);

  const handleGenerate = () => {
    setGenerating(true);
    appCRUD
      .generateTimeTable(dataId)
      .then((resp) => {
        const conflicts = resp.data.conflicts?.length || 0;
        openNotification(
          "success",
          "Success",
          `Timetable generated! ${resp.data.scheduled_count} sessions scheduled${conflicts ? `, ${conflicts} conflicts` : ""}.`
        );
        loadConflicts();
        if (activeTab === "day") {
          loadDayData(day);
        } else {
          loadAllData();
        }
      })
      .catch((error) => {
        openNotification("error", "Error", error.response?.data?.error || "Failed to generate timetable");
      })
      .finally(() => {
        setGenerating(false);
      });
  };

  // Get unique values for filters from all data
  const currentData = activeTab === "day" ? data : allData;
  const rooms = [...new Set(currentData.map(item => item.room?.code || item.room))].filter(Boolean).sort();
  const programmes = [...new Set(currentData.map(item => item.programme?.code))].filter(Boolean).sort();
  const studyYears = [...new Set(currentData.map(item => item.study))].filter(Boolean).sort();

  // Normalize instructor name for deduplication
  // Use first name + second name/initial to distinguish different people with same first name
  const normalizeInstructorName = (name) => {
    if (!name) return "";
    let normalized = name
      .toLowerCase()
      // Remove titles
      .replace(/^(dr\.?|prof\.?|mr\.?|ms\.?|mrs\.?)\s*/i, "")
      // Remove common suffixes like "combined with cs", "combined with it", etc.
      .replace(/\s+combined\s+with\s+\w+/gi, "")
      // Remove periods
      .replace(/\./g, "")
      // Normalize multiple spaces
      .replace(/\s+/g, " ")
      .trim();

    // Get first two words (first name + second name/initial) for deduplication
    const parts = normalized.split(/[\s-]+/);
    const key = parts.slice(0, 2).join(" ");

    // Normalize common spelling variations
    return key
      .replace(/abdulr/g, "abdur")
      .replace(/abdul/g, "abdu")
      .replace(/muhammad/g, "mohamed")
      .replace(/mohammed/g, "mohamed");
  };

  // Get unique instructors by normalized name, keeping the best display name
  const getUniqueInstructors = () => {
    const instructorMap = new Map();
    currentData.forEach(item => {
      const rawName = item.instractor_name || item.instractor;
      if (!rawName) return;

      const normalized = normalizeInstructorName(rawName);
      const currentBest = instructorMap.get(normalized);

      // Score the name - prefer cleaner names without "combined with" etc.
      const hasJunk = /combined\s+with/i.test(rawName);
      const hasTitle = /^(dr\.?|prof\.?)/i.test(rawName);

      // If no current best, or current best has junk but this one doesn't, or this one has title
      const shouldReplace = !currentBest ||
        (currentBest.hasJunk && !hasJunk) ||
        (!currentBest.hasTitle && hasTitle && !hasJunk);

      if (shouldReplace) {
        instructorMap.set(normalized, { name: rawName, hasJunk, hasTitle });
      }
    });

    // Return array of {normalized, display} objects sorted by display name
    return Array.from(instructorMap.entries())
      .map(([normalized, data]) => ({ normalized, display: data.name }))
      .sort((a, b) => a.display.localeCompare(b.display));
  };

  const instructors = getUniqueInstructors();

  // Get display name for selected instructor filter
  const getInstructorDisplayName = (normalizedName) => {
    const found = instructors.find(i => i.normalized === normalizedName);
    return found ? found.display : normalizedName;
  };

  // Filter data based on active tab and filters
  const getFilteredData = () => {
    let filtered = currentData;

    if (activeTab === "instructor" && instructorFilter) {
      // Match by normalized name to catch all variations
      filtered = filtered.filter(item => {
        const itemName = item.instractor_name || item.instractor;
        return normalizeInstructorName(itemName) === instructorFilter;
      });
    }

    if (activeTab === "cohort") {
      if (programmeFilter) {
        filtered = filtered.filter(item => item.programme?.code === programmeFilter);
      }
      if (studyYearFilter) {
        filtered = filtered.filter(item => item.study === studyYearFilter);
      }
    }

    if (activeTab === "day") {
      if (programmeFilter) {
        filtered = filtered.filter(item => item.programme?.code === programmeFilter);
      }
      if (roomFilter) {
        filtered = filtered.filter(item => (item.room?.code || item.room) === roomFilter);
      }
    }

    return filtered;
  };

  const filteredData = getFilteredData();

  // Group data by day for weekly view
  const getDataByDay = (dayName) => {
    return filteredData.filter(item =>
      item.day?.toLowerCase() === dayName.toLowerCase()
    );
  };

  // Statistics
  const stats = {
    totalSessions: filteredData.length,
    programmes: [...new Set(filteredData.map(item => item.programme?.code))].filter(Boolean).length,
    rooms: [...new Set(filteredData.map(item => item.room?.code || item.room))].filter(Boolean).length,
    totalHours: filteredData.reduce((sum, item) => {
      const start = parseInt(item.start_time?.slice(0, 2) || 0);
      const end = parseInt(item.end_time?.slice(0, 2) || 0);
      return sum + (end - start);
    }, 0),
    totalStudents: filteredData.reduce((sum, item) => sum + (item.student || 0), 0),
  };

  const getProgrammeColor = (programmeCode) => {
    return PROGRAMME_COLORS[programmeCode] || PROGRAMME_COLORS.default;
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    return timeStr.slice(0, 5);
  };

  // Drag and Drop Handlers
  const handleDragStart = (e, session) => {
    if (!editMode) return;
    setDraggedItem(session);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", session.id);
    e.target.style.opacity = "0.5";
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = "1";
    setDraggedItem(null);
    setDropTarget(null);
  };

  const handleDragOver = (e, targetDay, targetHour, targetRoom) => {
    if (!editMode || !draggedItem) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDropTarget({ day: targetDay, hour: targetHour, room: targetRoom });
  };

  const handleDragLeave = () => {
    setDropTarget(null);
  };

  const handleDrop = async (e, targetDay, targetHour, targetRoom) => {
    e.preventDefault();
    if (!editMode || !draggedItem) return;

    setDropTarget(null);

    const duration = draggedItem.duration || 2;
    const newStartTime = `${targetHour.toString().padStart(2, '0')}:00:00`;
    const newEndTime = `${(targetHour + duration).toString().padStart(2, '0')}:00:00`;

    // Find the room object
    const roomObj = availableRooms.find(r => r.code === targetRoom);
    if (!roomObj) {
      message.error("Room not found");
      return;
    }

    // Check for conflicts
    setUpdating(true);
    try {
      const conflictResponse = await appCRUD.checkConflict({
        table_id: dataId,
        day: targetDay.toLowerCase(),
        start_time: newStartTime,
        end_time: newEndTime,
        room_id: roomObj.id,
        instructor_id: draggedItem.instractor,
        programme_id: draggedItem.programme?.id,
        study_year: draggedItem.study,
        exclude_id: draggedItem.id
      });

      const conflicts = conflictResponse.data.conflicts || [];

      if (conflicts.length > 0) {
        setConflictCheckResult({
          session: draggedItem,
          targetDay,
          targetHour,
          targetRoom,
          conflicts,
          newStartTime,
          newEndTime,
          roomId: roomObj.id
        });
        message.warning(`${conflicts.length} conflict(s) detected. Please review.`);
      } else {
        // No conflicts, proceed with update
        await updateSession(draggedItem.id, {
          day: targetDay.toLowerCase(),
          start_time: newStartTime,
          end_time: newEndTime,
          room: roomObj.id
        });
      }
    } catch (error) {
      message.error("Failed to check conflicts: " + (error.response?.data?.detail || error.message));
    } finally {
      setUpdating(false);
    }
  };

  // Update session
  const updateSession = async (sessionId, updates) => {
    setUpdating(true);
    try {
      await appCRUD.updateTableDay(sessionId, updates);
      message.success("Session updated successfully");
      setConflictCheckResult(null);
      // Reload data
      if (activeTab === "day") {
        loadDayData(day);
      } else {
        loadAllData();
      }
    } catch (error) {
      message.error("Failed to update: " + (error.response?.data?.detail || error.message));
    } finally {
      setUpdating(false);
    }
  };

  // Delete session
  const handleDeleteSession = async (sessionId) => {
    setUpdating(true);
    try {
      await appCRUD.deleteTableDay(sessionId);
      message.success("Session deleted");
      if (activeTab === "day") {
        loadDayData(day);
      } else {
        loadAllData();
      }
    } catch (error) {
      message.error("Failed to delete: " + (error.response?.data?.detail || error.message));
    } finally {
      setUpdating(false);
    }
  };

  // Session Card with edit/delete actions and student count
  const SessionContent = ({ session, showDragHandle = true }) => {
    const colors = getProgrammeColor(session.programme?.code);
    const studentCount = session.student || 0;

    return (
      <div
        className={`session-content ${editMode ? 'draggable' : ''}`}
        draggable={editMode}
        onDragStart={(e) => handleDragStart(e, session)}
        onDragEnd={handleDragEnd}
        style={{ cursor: editMode ? 'grab' : 'default' }}
      >
        <div style={{ fontWeight: "bold", color: colors.text, fontSize: 12 }}>
          {editMode && showDragHandle && <DragOutlined style={{ marginRight: 4 }} />}
          {session.course}
        </div>
        <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>
          <HomeOutlined style={{ marginRight: 4 }} />
          {session.room?.code || session.room}
        </div>
        <div style={{ fontSize: 10, color: "#888", marginTop: 2 }}>
          <UserOutlined style={{ marginRight: 4 }} />
          {char.toTitleCase((session.instractor_name || session.instractor || "").substring(0, 15))}
        </div>
        <div style={{ marginTop: 4 }}>
          <Tag color="blue" style={{ fontSize: 9, marginRight: 2, padding: "0 4px" }}>
            {session.programme?.code}
          </Tag>
          <Tag
            style={{
              fontSize: 9,
              padding: "0 4px",
              backgroundColor: YEAR_COLORS[session.study] || "#666",
              color: "#fff",
              border: "none",
            }}
          >
            Y{session.study}
          </Tag>
        </div>
        {/* Student Count Badge */}
        <div style={{ marginTop: 4 }}>
          <Tooltip title={`Expected ${studentCount} students`}>
            <Tag
              icon={<TeamOutlined />}
              color="cyan"
              style={{ fontSize: 9, padding: "0 4px" }}
            >
              {studentCount} students
            </Tag>
          </Tooltip>
        </div>
        {/* Edit/Delete actions in edit mode */}
        {editMode && (
          <div style={{ marginTop: 6, borderTop: "1px solid #eee", paddingTop: 4 }}>
            <Space size={4}>
              <Popconfirm
                title="Delete this session?"
                onConfirm={() => handleDeleteSession(session.id)}
                okText="Yes"
                cancelText="No"
              >
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  style={{ fontSize: 10 }}
                />
              </Popconfirm>
            </Space>
          </div>
        )}
      </div>
    );
  };

  // Conflict resolution dialog
  const ConflictDialog = () => {
    if (!conflictCheckResult) return null;

    const { session, conflicts, targetDay, targetHour, newStartTime, newEndTime, roomId } = conflictCheckResult;

    return (
      <Modal open={true} onClose={() => setConflictCheckResult(null)} size="sm">
        <Modal.Header>
          <Modal.Title>
            <WarningOutlined style={{ color: "#faad14", marginRight: 8 }} />
            Conflict Detected
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <p>Moving <strong>{session.course}</strong> to {targetDay} at {targetHour}:00 would cause conflicts:</p>
            <ul className="list-group">
              {conflicts.map((conflict, idx) => (
                <li key={idx} className="list-group-item list-group-item-warning">
                  <strong>{conflict.type}</strong>: {conflict.message}
                  <br />
                  <small>Existing: {conflict.existing_course} ({conflict.existing_time})</small>
                </li>
              ))}
            </ul>
          </div>
          <p className="text-muted">Do you want to proceed anyway? This may cause scheduling issues.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setConflictCheckResult(null)}>
            Cancel
          </Button>
          <Button
            type="primary"
            danger
            loading={updating}
            onClick={() => updateSession(session.id, {
              day: targetDay.toLowerCase(),
              start_time: newStartTime,
              end_time: newEndTime,
              room: roomId
            })}
          >
            Move Anyway
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  // Weekly Grid View Component (for instructor/cohort views)
  const WeeklyGridView = () => {
    return (
      <div className="weekly-grid-container" style={{ overflowX: "auto" }}>
        <table className="timetable-grid" style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ ...headerStyle, minWidth: 60 }}>
                <ClockCircleOutlined /> Time
              </th>
              {days.map((d) => (
                <th key={d} style={{ ...headerStyle, minWidth: 150 }}>
                  {d}
                  <Badge
                    count={getDataByDay(d).length}
                    style={{ backgroundColor: "#1890ff", marginLeft: 8 }}
                    size="small"
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {hours.map((hour) => (
              <tr key={hour}>
                <td style={timeCellStyle}>
                  {`${hour}:00`}
                </td>
                {days.map((dayName) => {
                  const dayData = getDataByDay(dayName);
                  const sessions = dayData.filter((item) => {
                    const sessionStart = parseInt(item.start_time?.slice(0, 2) || 0);
                    const sessionEnd = parseInt(item.end_time?.slice(0, 2) || 0);
                    return sessionStart <= hour && sessionEnd > hour;
                  });

                  const startingSession = sessions.find(
                    (s) => parseInt(s.start_time?.slice(0, 2) || 0) === hour
                  );

                  if (startingSession) {
                    const start = parseInt(startingSession.start_time?.slice(0, 2) || 0);
                    const end = parseInt(startingSession.end_time?.slice(0, 2) || 0);
                    const duration = end - start;
                    const colors = getProgrammeColor(startingSession.programme?.code);

                    return (
                      <td
                        key={`${dayName}-${hour}`}
                        rowSpan={duration}
                        style={{
                          ...sessionCellStyle,
                          backgroundColor: colors.bg,
                          borderLeft: `4px solid ${colors.border}`,
                        }}
                      >
                        <SessionContent session={startingSession} showDragHandle={activeTab !== "instructor"} />
                      </td>
                    );
                  } else if (sessions.length > 0) {
                    return null; // Part of a spanning session
                  }

                  return <td key={`${dayName}-${hour}`} style={emptyCellStyle}></td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Daily Grid View Component (rooms as rows)
  const DailyGridView = () => {
    const displayRooms = roomFilter ? [roomFilter] : rooms;
    const isDropTarget = (targetRoom, targetHour) => {
      return dropTarget &&
             dropTarget.day === day &&
             dropTarget.room === targetRoom &&
             dropTarget.hour === targetHour;
    };

    return (
      <div className="timetable-grid-container" style={{ overflowX: "auto" }}>
        <table className="timetable-grid" style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={headerStyle}>
                <HomeOutlined /> Room
              </th>
              {hours.map((hour) => (
                <th key={hour} style={headerStyle}>
                  {`${hour}:00`}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayRooms.length === 0 ? (
              <tr>
                <td colSpan={hours.length + 1} style={{ textAlign: "center", padding: 40 }}>
                  <Empty description="No sessions scheduled for this day" />
                </td>
              </tr>
            ) : (
              displayRooms.map((room) => (
                <tr key={room}>
                  <td style={roomCellStyle}>
                    <Tooltip title={getRoomFullName(room)}>
                      <strong>{room}</strong>
                    </Tooltip>
                  </td>
                  {hours.map((hour) => {
                    const sessions = filteredData.filter((item) => {
                      const roomCode = item.room?.code || item.room;
                      const sessionStart = parseInt(item.start_time?.slice(0, 2) || 0);
                      const sessionEnd = parseInt(item.end_time?.slice(0, 2) || 0);
                      return roomCode === room && sessionStart <= hour && sessionEnd > hour;
                    });

                    const startingSession = sessions.find(
                      (s) => parseInt(s.start_time?.slice(0, 2) || 0) === hour
                    );

                    if (startingSession) {
                      const start = parseInt(startingSession.start_time?.slice(0, 2) || 0);
                      const end = parseInt(startingSession.end_time?.slice(0, 2) || 0);
                      const duration = end - start;
                      const colors = getProgrammeColor(startingSession.programme?.code);

                      return (
                        <td
                          key={`${room}-${hour}`}
                          colSpan={duration}
                          style={{
                            ...sessionCellStyle,
                            backgroundColor: colors.bg,
                            borderLeft: `4px solid ${colors.border}`,
                          }}
                        >
                          <SessionContent session={startingSession} />
                        </td>
                      );
                    } else if (sessions.length > 0) {
                      return null;
                    }

                    // Empty cell - droppable target in edit mode
                    const isTarget = isDropTarget(room, hour);
                    return (
                      <td
                        key={`${room}-${hour}`}
                        style={{
                          ...emptyCellStyle,
                          backgroundColor: isTarget ? "#e6f7ff" : "#fff",
                          border: isTarget ? "2px dashed #1890ff" : "1px solid #f0f0f0",
                        }}
                        onDragOver={(e) => handleDragOver(e, day, hour, room)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, day, hour, room)}
                      >
                        {editMode && (
                          <div style={{
                            height: 40,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#ccc",
                            fontSize: 10
                          }}>
                            {isTarget ? "Drop here" : ""}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    );
  };

  // List View Component
  const ListView = () => {
    const dataToShow = activeTab === "day" ? filteredData : filteredData;

    // Group by day for weekly views
    const groupedByDay = {};
    dataToShow.forEach(session => {
      const d = session.day || "Unknown";
      if (!groupedByDay[d]) groupedByDay[d] = [];
      groupedByDay[d].push(session);
    });

    return (
      <div className="timetable-list">
        {dataToShow.length === 0 ? (
          <Empty description="No sessions found" />
        ) : activeTab === "day" ? (
          <div className="row">
            {dataToShow
              .sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""))
              .map((session, idx) => (
                <SessionCard key={idx} session={session} />
              ))}
          </div>
        ) : (
          days.map(dayName => {
            const daySessions = groupedByDay[dayName.toLowerCase()] || [];
            if (daySessions.length === 0) return null;
            return (
              <div key={dayName} className="mb-4">
                <h5 className="mb-3">
                  <CalendarOutlined className="me-2" />
                  {dayName}
                  <Badge count={daySessions.length} style={{ backgroundColor: "#1890ff", marginLeft: 8 }} />
                </h5>
                <div className="row">
                  {daySessions
                    .sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""))
                    .map((session, idx) => (
                      <SessionCard key={idx} session={session} />
                    ))}
                </div>
              </div>
            );
          })
        )}
      </div>
    );
  };

  // Session Card Component
  const SessionCard = ({ session }) => {
    const colors = getProgrammeColor(session.programme?.code);
    const studentCount = session.student || 0;

    return (
      <div className="col-md-6 col-lg-4 mb-3">
        <Card
          size="small"
          style={{
            borderLeft: `4px solid ${colors.border}`,
            backgroundColor: colors.bg,
          }}
          extra={editMode && (
            <Popconfirm
              title="Delete this session?"
              onConfirm={() => handleDeleteSession(session.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button type="text" size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        >
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <h6 style={{ margin: 0, color: colors.text }}>
                {session.course}
              </h6>
              <p style={{ margin: "4px 0", color: "#666", fontSize: 13 }}>
                <UserOutlined className="me-1" />
                {char.toTitleCase(session.instractor_name || session.instractor || "")}
              </p>
            </div>
            <Tag color="blue">{session.programme?.code}</Tag>
          </div>
          <Divider style={{ margin: "8px 0" }} />
          <Row gutter={8}>
            <Col span={8}>
              <ClockCircleOutlined /> {formatTime(session.start_time)} - {formatTime(session.end_time)}
            </Col>
            <Col span={8}>
              <HomeOutlined /> {session.room?.code || session.room}
            </Col>
            <Col span={8}>
              <Tag
                style={{
                  backgroundColor: YEAR_COLORS[session.study] || "#666",
                  color: "#fff",
                  border: "none",
                }}
              >
                Year {session.study}
              </Tag>
            </Col>
          </Row>
          {/* Student Count Row */}
          <Row className="mt-2">
            <Col span={24}>
              <Tag icon={<TeamOutlined />} color="cyan">
                {studentCount} students expected
              </Tag>
            </Col>
          </Row>
        </Card>
      </div>
    );
  };

  const getRoomFullName = (roomCode) => {
    const roomData = currentData.find(d => (d.room?.code || d.room) === roomCode);
    return roomData?.room?.name || roomCode;
  };

  // Tab items
  const tabItems = [
    {
      key: "day",
      label: (
        <span>
          <CalendarOutlined /> Daily View
        </span>
      ),
      children: (
        <>
          {/* Day View Controls */}
          <Card size="small" className="mb-3">
            <Row gutter={16} align="middle">
              <Col xs={24} sm={5}>
                <Select
                  value={day}
                  onChange={setDay}
                  style={{ width: "100%" }}
                  size="large"
                >
                  {days.map((d) => (
                    <Option key={d} value={d}>{d}</Option>
                  ))}
                </Select>
              </Col>
              <Col xs={12} sm={4}>
                <Select
                  placeholder="Filter by Programme"
                  allowClear
                  style={{ width: "100%" }}
                  onChange={setProgrammeFilter}
                  value={programmeFilter}
                >
                  {programmes.map((p) => (
                    <Option key={p} value={p}>{p}</Option>
                  ))}
                </Select>
              </Col>
              <Col xs={12} sm={4}>
                <Select
                  placeholder="Filter by Room"
                  allowClear
                  style={{ width: "100%" }}
                  onChange={setRoomFilter}
                  value={roomFilter}
                >
                  {rooms.map((r) => (
                    <Option key={r} value={r}>{r}</Option>
                  ))}
                </Select>
              </Col>
              <Col xs={8} sm={3}>
                <Segmented
                  options={[
                    { value: "grid", icon: <TableOutlined /> },
                    { value: "list", icon: <UnorderedListOutlined /> },
                  ]}
                  value={viewMode}
                  onChange={setViewMode}
                />
              </Col>
              <Col xs={8} sm={4}>
                <Button
                  type={editMode ? "primary" : "default"}
                  icon={<EditOutlined />}
                  onClick={() => setEditMode(!editMode)}
                  danger={editMode}
                >
                  {editMode ? "Exit Edit" : "Edit Mode"}
                </Button>
              </Col>
              <Col xs={8} sm={4}>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    setProgrammeFilter(null);
                    setRoomFilter(null);
                    loadDayData(day);
                  }}
                >
                  Reset
                </Button>
              </Col>
            </Row>
          </Card>

          {/* Edit Mode Instructions */}
          {editMode && (
            <Card size="small" className="mb-3" style={{ backgroundColor: "#fff7e6", borderColor: "#ffc53d" }}>
              <Row align="middle">
                <Col span={24}>
                  <DragOutlined style={{ marginRight: 8, color: "#fa8c16" }} />
                  <strong>Edit Mode Active:</strong> Drag sessions to move them. Changes are saved automatically.
                  {updating && <Tag color="processing" className="ms-2">Updating...</Tag>}
                </Col>
              </Row>
            </Card>
          )}

          {/* Day View Content */}
          <Card>
            <h4 className="text-center mb-3">{day} Timetable</h4>
            {loading ? (
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "40vh" }}>
                <Loader size="md" speed="slow" content="Loading timetable..." vertical />
              </div>
            ) : data.length === 0 ? (
              <div className="text-center py-5">
                <Empty description="No timetable generated yet" />
                <Popconfirm
                  title="Generate Timetable"
                  description="This will auto-generate the teaching schedule based on allocations."
                  onConfirm={handleGenerate}
                  okText="Generate"
                  cancelText="Cancel"
                >
                  <Button
                    type="primary"
                    icon={<PlayCircleOutlined />}
                    loading={generating}
                    size="large"
                    className="mt-3"
                  >
                    Generate Timetable
                  </Button>
                </Popconfirm>
              </div>
            ) : (
              viewMode === "grid" ? <DailyGridView /> : <ListView />
            )}
          </Card>
        </>
      ),
    },
    {
      key: "instructor",
      label: (
        <span>
          <UserOutlined /> Instructor View
        </span>
      ),
      children: (
        <>
          {/* Instructor View Controls */}
          <Card size="small" className="mb-3">
            <Row gutter={16} align="middle">
              <Col xs={24} sm={10}>
                <Select
                  placeholder="Select Instructor"
                  showSearch
                  allowClear
                  style={{ width: "100%" }}
                  size="large"
                  onChange={setInstructorFilter}
                  value={instructorFilter}
                  optionFilterProp="children"
                >
                  {instructors.map((inst) => (
                    <Option key={inst.normalized} value={inst.normalized}>
                      {char.toTitleCase(inst.display)}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={12} sm={4}>
                <Segmented
                  options={[
                    { value: "grid", icon: <TableOutlined /> },
                    { value: "list", icon: <UnorderedListOutlined /> },
                  ]}
                  value={viewMode}
                  onChange={setViewMode}
                />
              </Col>
              <Col xs={12} sm={4}>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    setInstructorFilter(null);
                    loadAllData();
                  }}
                >
                  Reset
                </Button>
              </Col>
            </Row>
          </Card>

          {/* Instructor Info */}
          {instructorFilter && (
            <Card size="small" className="mb-3" style={{ backgroundColor: "#f0f5ff" }}>
              <Row align="middle">
                <Col span={12}>
                  <h5 className="mb-0">
                    <UserOutlined className="me-2" />
                    {char.toTitleCase(getInstructorDisplayName(instructorFilter))}
                  </h5>
                </Col>
                <Col span={12} className="text-end">
                  <Space>
                    <Tag color="blue">{filteredData.length} Sessions</Tag>
                    <Tag color="green">{stats.totalHours} Hours/Week</Tag>
                    <Tag color="purple">{stats.programmes} Programmes</Tag>
                    <Tag color="cyan">{stats.totalStudents} Students</Tag>
                  </Space>
                </Col>
              </Row>
            </Card>
          )}

          {/* Instructor View Content */}
          <Card>
            <h4 className="text-center mb-3">
              {instructorFilter ? `${char.toTitleCase(getInstructorDisplayName(instructorFilter))}'s Weekly Timetable` : "Select an Instructor"}
            </h4>
            {loading ? (
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "40vh" }}>
                <Loader size="md" speed="slow" content="Loading timetable..." vertical />
              </div>
            ) : !instructorFilter ? (
              <Empty description="Please select an instructor to view their weekly schedule" />
            ) : filteredData.length === 0 ? (
              <Empty description="No sessions found for this instructor" />
            ) : (
              viewMode === "grid" ? <WeeklyGridView /> : <ListView />
            )}
          </Card>
        </>
      ),
    },
    {
      key: "cohort",
      label: (
        <span>
          <TeamOutlined /> Programme/Year View
        </span>
      ),
      children: (
        <>
          {/* Cohort View Controls */}
          <Card size="small" className="mb-3">
            <Row gutter={16} align="middle">
              <Col xs={24} sm={8}>
                <Select
                  placeholder="Select Programme"
                  showSearch
                  allowClear
                  style={{ width: "100%" }}
                  size="large"
                  onChange={(val) => {
                    setProgrammeFilter(val);
                  }}
                  value={programmeFilter}
                  optionFilterProp="children"
                >
                  {programmes.map((prog) => (
                    <Option key={prog} value={prog}>{prog}</Option>
                  ))}
                </Select>
              </Col>
              <Col xs={12} sm={6}>
                <Select
                  placeholder="Select Year"
                  allowClear
                  style={{ width: "100%" }}
                  size="large"
                  onChange={setStudyYearFilter}
                  value={studyYearFilter}
                >
                  {studyYears.map((year) => (
                    <Option key={year} value={year}>Year {year}</Option>
                  ))}
                </Select>
              </Col>
              <Col xs={6} sm={4}>
                <Segmented
                  options={[
                    { value: "grid", icon: <TableOutlined /> },
                    { value: "list", icon: <UnorderedListOutlined /> },
                  ]}
                  value={viewMode}
                  onChange={setViewMode}
                />
              </Col>
              <Col xs={6} sm={4}>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    setProgrammeFilter(null);
                    setStudyYearFilter(null);
                    loadAllData();
                  }}
                >
                  Reset
                </Button>
              </Col>
            </Row>
          </Card>

          {/* Cohort Info */}
          {(programmeFilter || studyYearFilter) && (
            <Card size="small" className="mb-3" style={{ backgroundColor: "#f6ffed" }}>
              <Row align="middle">
                <Col span={12}>
                  <h5 className="mb-0">
                    <TeamOutlined className="me-2" />
                    {programmeFilter || "All Programmes"}
                    {studyYearFilter && ` - Year ${studyYearFilter}`}
                  </h5>
                </Col>
                <Col span={12} className="text-end">
                  <Space>
                    <Tag color="blue">{filteredData.length} Sessions</Tag>
                    <Tag color="green">{stats.totalHours} Hours/Week</Tag>
                    <Tag color="orange">{stats.rooms} Rooms</Tag>
                    <Tag color="cyan">{stats.totalStudents} Students</Tag>
                  </Space>
                </Col>
              </Row>
            </Card>
          )}

          {/* Cohort View Content */}
          <Card>
            <h4 className="text-center mb-3">
              {programmeFilter || studyYearFilter
                ? `${programmeFilter || "All"} ${studyYearFilter ? `Year ${studyYearFilter}` : ""} Weekly Timetable`
                : "Select Programme and/or Year"
              }
            </h4>
            {loading ? (
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "40vh" }}>
                <Loader size="md" speed="slow" content="Loading timetable..." vertical />
              </div>
            ) : (!programmeFilter && !studyYearFilter) ? (
              <Empty description="Please select a programme and/or year to view their weekly schedule" />
            ) : filteredData.length === 0 ? (
              <Empty description="No sessions found for this selection" />
            ) : (
              viewMode === "grid" ? <WeeklyGridView /> : <ListView />
            )}
          </Card>
        </>
      ),
    },
  ];

  return (
    <Modal open={open} onClose={() => setOpen(false)} size="full">
      <Modal.Header>
        <Modal.Title>
          <CalendarOutlined className="me-2" />
          Teaching Timetable
        </Modal.Title>
      </Modal.Header>
      <Divider />
      <Modal.Body>
        <div className="container-fluid">
          {/* Statistics Row */}
          <Row gutter={16} className="mb-3">
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic
                  title="Sessions"
                  value={stats.totalSessions}
                  prefix={<BookOutlined />}
                  valueStyle={{ color: "#1890ff" }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic
                  title="Teaching Hours"
                  value={stats.totalHours}
                  prefix={<ClockCircleOutlined />}
                  valueStyle={{ color: "#52c41a" }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic
                  title="Total Students"
                  value={stats.totalStudents}
                  prefix={<TeamOutlined />}
                  valueStyle={{ color: "#722ed1" }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic
                  title="Rooms Used"
                  value={stats.rooms}
                  prefix={<HomeOutlined />}
                  valueStyle={{ color: "#fa8c16" }}
                />
              </Card>
            </Col>
          </Row>

          {/* Programme Legend */}
          <Card size="small" className="mb-3">
            <Space wrap>
              <span style={{ fontWeight: "bold" }}>Programmes:</span>
              {Object.entries(PROGRAMME_COLORS).filter(([k]) => k !== "default").map(([code, colors]) => (
                <Tag
                  key={code}
                  style={{
                    backgroundColor: colors.bg,
                    borderColor: colors.border,
                    color: colors.text,
                  }}
                >
                  {code}
                </Tag>
              ))}
            </Space>
          </Card>

          {/* Main Tabs */}
          <Tabs
            activeKey={activeTab}
            onChange={(key) => {
              setActiveTab(key);
              // Reset filters when switching tabs
              setProgrammeFilter(null);
              setRoomFilter(null);
              setInstructorFilter(null);
              setStudyYearFilter(null);
              setEditMode(false);
            }}
            items={tabItems}
            type="card"
          />
        </div>
      </Modal.Body>
      <Divider style={{ marginTop: -10 }} />
      <Modal.Footer>
        <div className="container-fluid">
          <div className="row">
            <div className="col-md-6 col-sm-12 d-flex justify-content-start">
              {(data.length > 0 || allData.length > 0) && (
                <Space>
                  <Popconfirm
                    title="Regenerate Timetable"
                    description="This will clear and regenerate the entire timetable. Continue?"
                    onConfirm={handleGenerate}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button icon={<PlayCircleOutlined />} loading={generating}>
                      Regenerate
                    </Button>
                  </Popconfirm>
                  <Button
                    type="primary"
                    icon={<FilePdfOutlined />}
                    onClick={() =>
                      appCRUD.downloadTimetablePDF(dataId).catch(() =>
                        openNotification("error", "Download Failed", "Could not download PDF")
                      )
                    }
                  >
                    PDF
                  </Button>
                  <Button
                    style={{ backgroundColor: "#52c41a", borderColor: "#52c41a", color: "#fff" }}
                    icon={<FileExcelOutlined />}
                    onClick={() =>
                      appCRUD.downloadTimetableExcel(dataId).catch(() =>
                        openNotification("error", "Download Failed", "Could not download Excel")
                      )
                    }
                  >
                    Excel
                  </Button>
                  <Button
                    icon={<CalendarOutlined />}
                    onClick={() =>
                      appCRUD.downloadTimetableICal(dataId).catch(() =>
                        openNotification("error", "Download Failed", "Could not download Calendar")
                      )
                    }
                  >
                    Calendar
                  </Button>
                  {conflictsData?.conflict_count > 0 && (
                    <Badge count={conflictsData.conflict_count} overflowCount={999}>
                      <Button
                        danger
                        icon={<WarningOutlined />}
                        onClick={() => setConflictsOpen(true)}
                      >
                        Conflicts
                      </Button>
                    </Badge>
                  )}
                </Space>
              )}
            </div>
            <div className="col-md-6 col-sm-12 d-flex justify-content-end">
              <Button
                danger
                onClick={() => setOpen(false)}
              >
                <i className="ri-close-circle-line align-middle fs-16 me-2"></i>
                Close
              </Button>
            </div>
          </div>
        </div>
      </Modal.Footer>

      {/* Conflict Resolution Dialog */}
      <ConflictDialog />

      {/* Unscheduled Conflicts Modal */}
      <Modal
        open={conflictsOpen}
        onClose={() => setConflictsOpen(false)}
        size="lg"
      >
        <Modal.Header>
          <Modal.Title>
            <WarningOutlined style={{ color: "#ff4d4f", marginRight: 8 }} />
            Unscheduled Sessions ({conflictsData?.conflict_count || 0})
          </Modal.Title>
        </Modal.Header>
        <Divider />
        <Modal.Body>
          {loadingConflicts ? (
            <Loader content="Loading conflicts..." />
          ) : !conflictsData || conflictsData.conflict_count === 0 ? (
            <Empty description="No conflicts - all sessions scheduled!" />
          ) : (
            <>
              <Row gutter={16} className="mb-3">
                <Col span={8}>
                  <Card size="small">
                    <Statistic
                      title="Scheduled"
                      value={conflictsData.scheduled_count}
                      valueStyle={{ color: "#52c41a" }}
                    />
                  </Card>
                </Col>
                <Col span={8}>
                  <Card size="small">
                    <Statistic
                      title="Conflicts"
                      value={conflictsData.conflict_count}
                      valueStyle={{ color: "#ff4d4f" }}
                    />
                  </Card>
                </Col>
                <Col span={8}>
                  <Card size="small">
                    <Statistic
                      title="Programmes Affected"
                      value={Object.keys(conflictsData.grouped_by_programme || {}).length}
                      valueStyle={{ color: "#faad14" }}
                    />
                  </Card>
                </Col>
              </Row>

              <Card size="small" className="mb-3" title="Summary by Reason">
                {Object.entries(conflictsData.reason_summary || {}).map(([reason, count]) => (
                  <div key={reason} className="d-flex justify-content-between py-1">
                    <span>{reason}</span>
                    <Tag color="red">{count}</Tag>
                  </div>
                ))}
              </Card>

              <Card size="small" title="Conflicts by Programme">
                <div style={{ maxHeight: 400, overflowY: "auto" }}>
                  {Object.entries(conflictsData.grouped_by_programme || {})
                    .sort((a, b) => b[1].length - a[1].length)
                    .map(([prog, items]) => (
                      <div key={prog} className="mb-2">
                        <div className="d-flex align-items-center mb-1">
                          <Tag color="blue" style={{ fontWeight: "bold" }}>{prog}</Tag>
                          <span className="text-muted">({items.length} conflicts)</span>
                        </div>
                        <div style={{ paddingLeft: 16 }}>
                          {items.map((c, i) => (
                            <div key={i} className="small text-muted py-1">
                              • <strong>{c.course}</strong> (Year {c.study_year}) - {c.hours}h - {c.reason}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                </div>
              </Card>
            </>
          )}
        </Modal.Body>
        <Divider style={{ marginTop: -10 }} />
        <Modal.Footer>
          <Button onClick={() => setConflictsOpen(false)}>Close</Button>
        </Modal.Footer>
      </Modal>
    </Modal>
  );
};

// Styles
const headerStyle = {
  padding: "10px 8px",
  backgroundColor: "#f5f5f5",
  borderBottom: "2px solid #d9d9d9",
  textAlign: "center",
  fontWeight: "bold",
  fontSize: 12,
  minWidth: 70,
};

const timeCellStyle = {
  padding: "8px 10px",
  backgroundColor: "#fafafa",
  borderRight: "2px solid #d9d9d9",
  fontWeight: "bold",
  fontSize: 12,
  textAlign: "center",
  minWidth: 60,
};

const roomCellStyle = {
  padding: "8px 10px",
  backgroundColor: "#fafafa",
  borderRight: "2px solid #d9d9d9",
  fontWeight: "bold",
  fontSize: 12,
  textAlign: "center",
  minWidth: 80,
};

const sessionCellStyle = {
  padding: "6px 8px",
  border: "1px solid #e8e8e8",
  verticalAlign: "top",
  minHeight: 60,
};

const emptyCellStyle = {
  padding: "6px 8px",
  border: "1px solid #f0f0f0",
  backgroundColor: "#fff",
  minHeight: 60,
};
